import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'contributor',
    display_name: '',
    is_active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        await api.put(`/auth/users/${editingUser.id}`, formData);
        toast.success('Utilisateur modifié');
      } else {
        await api.post('/auth/users', formData);
        toast.success('Utilisateur créé');
      }
      
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet utilisateur ?')) return;
    
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success('Utilisateur supprimé');
      loadUsers();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'redacteur',
      display_name: '',
      is_active: true
    });
  };

  return (
    <AdminLayout>
      <Helmet><title>Utilisateurs - Admin Blog</title></Helmet>
      
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Utilisateurs</h1>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500"
          >
            <i className="fas fa-user-plus mr-2"></i>Nouvel utilisateur
          </button>
        </div>

        <div className="bg-surface rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface/90 text-gray-400 text-sm">
              <tr>
                <th className="px-6 py-4 text-left">Utilisateur</th>
                <th className="px-6 py-4 text-left">Email</th>
                <th className="px-6 py-4 text-left">Rôle</th>
                <th className="px-6 py-4 text-left">Statut</th>
                <th className="px-6 py-4 text-left">Dernière connexion</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {users.map(user => (
                <tr key={user.id} className="border-t border-gray-700/30 hover:bg-surface/80">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-white">{user.display_name || user.username}</div>
                      <div className="text-sm text-gray-500">@{user.username}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : 'Contributeur'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : 'Jamais'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => { setEditingUser(user); setFormData({ ...user, password: '' }); setShowModal(true); }}
                      className="text-blue-400 hover:text-blue-300 mr-4"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button onClick={() => handleDelete(user.id)} className="text-red-400 hover:text-red-300">
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl p-6 max-w-lg w-full">
              <h2 className="text-2xl font-bold text-white mb-4">
                {editingUser ? 'Modifier' : 'Nouvel'} utilisateur
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Nom d'utilisateur"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                  required
                />
                <input
                  type="password"
                  placeholder={editingUser ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                  required={!editingUser}
                />
                <input
                  type="text"
                  placeholder="Nom d'affichage"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                >
                  <option value="contributor">Contributeur</option>
                  <option value="admin">Administrateur</option>
                </select>
                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  Compte actif
                </label>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-yellow-400 text-gray-900 py-3 rounded-lg font-semibold hover:bg-yellow-500">
                    {editingUser ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingUser(null); }}
                    className="px-6 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UsersManager;
